import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ShoppingBag } from 'lucide-react';
import type { CustomerJob } from './types';

const PAGE_SIZE = 5;

interface ActivityTabProps {
    jobs: CustomerJob[];
}

export function ActivityTab({ jobs }: ActivityTabProps) {
    const navigate = useNavigate();
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const visibleJobs = jobs.slice(0, visibleCount);

    return (
        <section className="bg-panel border border-line rounded-xl overflow-hidden">
            <div className="p-4 border-b border-line flex items-center justify-between bg-raised">
                <h3 className="font-bold text-fg flex items-center gap-2">
                    Activity
                </h3>
                {jobs.length > 0 && (
                    <span className="text-xs text-fg-4">{jobs.length} job{jobs.length !== 1 ? 's' : ''}</span>
                )}
            </div>
            <div className="divide-y divide-tint/5">
                {jobs.length === 0 ? (
                    <div className="p-8 text-center text-fg-4 text-sm">
                        No jobs found for this customer yet.
                    </div>
                ) : (
                    visibleJobs.map(job => {
                        // A counter sale has no vehicle, and the old heading was
                        // built purely out of vehicle fields — so it rendered as a
                        // bare "•" with nothing either side, over "No description".
                        // The sale was in the list all along; it just could not be
                        // read. Label it for what it is instead.
                        const vehicleLabel = job.vehicles?.vrm
                            ? `${job.vehicles.vrm} • ${[job.vehicles.make, job.vehicles.model].filter(Boolean).join(' ')}`.trim()
                            : null;
                        const detail = job.description || job.job_description || job.title || null;

                        return (
                        <div
                            key={job.id}
                            onClick={() => navigate(`/workshop?job_id=${job.id}`)}
                            className="p-4 hover:bg-tint/5 transition-colors group flex items-start justify-between gap-3 cursor-pointer"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-3.5 h-3.5 text-fg-4" />
                                    <span className="text-xs text-fg-3">
                                        {new Date(job.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                </div>
                                <h4 className="font-bold text-fg mt-1 flex items-center gap-2">
                                    {vehicleLabel ?? (
                                        <>
                                            <ShoppingBag className="w-3.5 h-3.5 text-accent" />
                                            {detail || 'Counter sale'}
                                        </>
                                    )}
                                </h4>
                                {(vehicleLabel || detail) && (
                                    <p className="text-sm text-fg-3 line-clamp-1 mt-0.5">
                                        {vehicleLabel ? (detail || 'No description') : 'Parts sold with no vehicle attached'}
                                    </p>
                                )}
                            </div>
                            <div className="text-right shrink-0">
                                <div className={`text-fd-3xs font-bold uppercase tracking-widest px-2 py-0.5 rounded ${job.status === 'ready' ? 'bg-success/10 text-success' : 'bg-info/10 text-info'
                                    }`}>
                                    {job.status}
                                </div>
                                <div className="text-xs text-accent hover:underline mt-2 font-medium">
                                    View Job
                                </div>
                            </div>
                        </div>
                        );
                    })
                )}
            </div>
            {jobs.length > visibleCount && (
                <button
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    className="w-full p-3 text-xs font-bold text-accent hover:bg-tint/5 transition-colors border-t border-tint/5"
                >
                    Load more ({jobs.length - visibleCount} remaining)
                </button>
            )}
        </section>
    );
}
